import {
  ErrorCode,
  Purchases,
  PurchasesError,
  type Offerings,
  type Package,
  type PurchaseResult,
} from '@revenuecat/purchases-js';
import { getSupabaseClient } from '../lib/supabase';
import type { WebEvent } from '../types/events';
import type { WebPurchaseCatalogItem } from '../domain/webPricingCatalog';

export type WebEventUpgradeSummary = {
  event_id: string;
  host_user_id: string;
  current_package_key: string;
  current_package_display_name: string;
  current_package_sort_order: number;
  current_guest_limit: number;
  current_photo_limit: number;
  current_host_video_limit: number;
  addon_host_video_limit: number;
  effective_host_video_limit: number;
  current_guest_count: number;
  current_photo_count: number;
  current_host_video_count: number;
  can_upgrade: boolean;
  next_package_key: string | null;
  next_package_display_name: string | null;
};

export type WebAccountStorageSummary = {
  user_id: string;
  total_storage_bytes: number | string;
  photo_storage_bytes: number | string;
  video_storage_bytes: number | string;
  total_events_counted: number;
  required_tier_key: string;
  required_display_name: string;
  required_price_label: string;
  required_usage_range_label: string;
  active_subscription_id: string | null;
  active_product_id: string | null;
  active_tier_key: string | null;
  active_status: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  is_subscription_active: boolean;
  is_billing_issue: boolean;
  needs_storage_subscription: boolean;
  is_required_tier_covered: boolean;
};

export type WebPurchaseOfferingState = {
  configured: boolean;
  offerings: Offerings | null;
  error: string | null;
};

export type WebPurchaseOutcome = {
  cancelled: boolean;
  purchaseResult: PurchaseResult | null;
  syncConfirmed: boolean;
  syncMessage: string;
};

const WEB_API_KEY = String(import.meta.env.VITE_REVENUECAT_WEB_API_KEY ?? '').trim();
const PACKAGE_SORT: Record<string, number> = {
  free_event: 0,
  small_event: 10,
  standard_event: 20,
  large_event: 30,
  premier_event: 40,
};

let purchasesInstance: Purchases | null = null;

function cleanText(value: unknown): string {
  return String(value ?? '').trim();
}

function firstRow<T>(value: unknown): T | null {
  if (Array.isArray(value)) return (value[0] as T | undefined) ?? null;
  return value && typeof value === 'object' ? (value as T) : null;
}

function purchaseIntentFor(item: WebPurchaseCatalogItem): string {
  switch (item.kind) {
    case 'event_package': return 'host_event_package';
    case 'event_upgrade': return 'host_event_upgrade';
    case 'host_video_addon': return 'host_video_addon';
    case 'guest_video_pass': return 'guest_video_pass';
    case 'account_storage': return 'account_storage';
    default: return 'web_purchase';
  }
}

export function isRevenueCatWebConfigured(): boolean {
  return WEB_API_KEY.length > 0;
}

async function getPurchases(userId: string): Promise<Purchases> {
  if (!WEB_API_KEY) {
    throw new Error('RevenueCat Web Billing is not configured for this deployment.');
  }
  if (!userId) throw new Error('Sign in before making a purchase.');

  if (!purchasesInstance) {
    purchasesInstance = Purchases.configure({
      apiKey: WEB_API_KEY,
      appUserId: userId,
    });
    return purchasesInstance;
  }

  if (purchasesInstance.getAppUserId() !== userId) {
    await purchasesInstance.changeUser(userId);
  }
  return purchasesInstance;
}

export async function loadWebPurchaseOfferings(userId: string): Promise<WebPurchaseOfferingState> {
  if (!WEB_API_KEY) return { configured: false, offerings: null, error: null };
  try {
    const purchases = await getPurchases(userId);
    return {
      configured: true,
      offerings: await purchases.getOfferings({ currency: 'USD' }),
      error: null,
    };
  } catch (error) {
    return {
      configured: true,
      offerings: null,
      error: error instanceof Error ? error.message : 'Unable to load RevenueCat Web offerings.',
    };
  }
}

export function resolveRevenueCatPackage(
  offerings: Offerings | null,
  item: WebPurchaseCatalogItem,
): Package | null {
  const offering = offerings?.all?.[item.offeringId];
  if (!offering) return null;
  return offering.packagesById?.[item.packageId] ?? null;
}

export function localizedWebPrice(
  offerings: Offerings | null,
  item: WebPurchaseCatalogItem,
): string {
  return resolveRevenueCatPackage(offerings, item)?.webBillingProduct?.price?.formattedPrice || item.fallbackPriceLabel;
}

export async function loadEventUpgradeSummary(eventId: string): Promise<WebEventUpgradeSummary | null> {
  const client = getSupabaseClient();
  const { data, error } = await client.rpc('get_event_upgrade_summary', { p_event_id: eventId });
  if (error) throw error;
  return firstRow<WebEventUpgradeSummary>(data);
}

export async function loadAccountStorageSummary(userId: string): Promise<WebAccountStorageSummary | null> {
  const client = getSupabaseClient();
  const { data, error } = await client.rpc('get_account_storage_billing_summary', { p_user_id: userId });
  if (error) throw error;
  return firstRow<WebAccountStorageSummary>(data);
}

async function guestVideoPassIsActive(eventId: string, userId: string, productId: string): Promise<boolean> {
  const client = getSupabaseClient();
  const { data, error } = await client
    .from('guest_video_entitlements')
    .select('id,status,product_id')
    .eq('event_id', eventId)
    .eq('guest_user_id', userId)
    .eq('product_id', productId)
    .eq('status', 'active')
    .limit(1);
  if (error) return false;
  return Boolean(data?.[0]?.id);
}

function sleep(milliseconds: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
}

async function waitForCanonicalPurchaseState(params: {
  item: WebPurchaseCatalogItem;
  event: WebEvent;
  userId: string;
  beforeUpgrade: WebEventUpgradeSummary | null;
}): Promise<{ confirmed: boolean; message: string }> {
  const deadline = Date.now() + 16_000;
  while (Date.now() < deadline) {
    try {
      if (params.item.kind === 'account_storage') {
        const storage = await loadAccountStorageSummary(params.userId);
        if (
          storage?.is_subscription_active &&
          (storage.active_product_id === params.item.productId || storage.active_tier_key === params.item.key)
        ) {
          return { confirmed: true, message: 'Storage subscription is active in Gathrd.' };
        }
      } else if (params.item.kind === 'guest_video_pass') {
        if (await guestVideoPassIsActive(params.event.id, params.userId, params.item.productId)) {
          return { confirmed: true, message: 'Guest video pass is active for this event.' };
        }
      } else {
        const upgrade = await loadEventUpgradeSummary(params.event.id);
        if (params.item.kind === 'host_video_addon') {
          const before = params.beforeUpgrade?.effective_host_video_limit ?? 0;
          const expected = before + (params.item.videoQuantityAdded ?? 0);
          if ((upgrade?.effective_host_video_limit ?? 0) >= expected) {
            return { confirmed: true, message: 'Host video capacity is updated.' };
          }
        } else if (params.item.packageKey) {
          const targetSort = PACKAGE_SORT[params.item.packageKey] ?? 0;
          const currentSort = upgrade?.current_package_sort_order ?? 0;
          if (currentSort >= targetSort) {
            return { confirmed: true, message: `${upgrade?.current_package_display_name || params.item.displayName} is active for this event.` };
          }
        }
      }
    } catch {
      // Webhook synchronization can briefly race the read-side state.
    }
    await sleep(800);
  }
  return {
    confirmed: false,
    message: 'Payment completed. Gathrd is still waiting for the verified RevenueCat webhook to finish syncing this purchase.',
  };
}

export async function purchaseWebCatalogItem(params: {
  item: WebPurchaseCatalogItem;
  event: WebEvent;
  userId: string;
  email?: string | null;
  offerings: Offerings | null;
}): Promise<WebPurchaseOutcome> {
  const rcPackage = resolveRevenueCatPackage(params.offerings, params.item);
  if (!rcPackage) {
    throw new Error(`RevenueCat Web offering “${params.item.offeringId}” is missing package “${params.item.packageId}”.`);
  }
  const actualProductId = cleanText(rcPackage.webBillingProduct?.identifier);
  if (actualProductId !== params.item.productId) {
    throw new Error(
      `RevenueCat Web package mismatch: ${params.item.packageId} is attached to ${actualProductId || 'no product'} instead of ${params.item.productId}.`,
    );
  }

  const isOrganizer = params.event.organizer_id === params.userId;
  if (['event_package', 'event_upgrade', 'host_video_addon', 'account_storage'].includes(params.item.kind) && !isOrganizer) {
    throw new Error('Only the event host can purchase event packages, host video add-ons, or account storage.');
  }
  if (params.item.kind === 'guest_video_pass' && isOrganizer) {
    throw new Error('Guest video passes are for event guests. Hosts should use host video add-ons.');
  }

  const purchases = await getPurchases(params.userId);
  const intent = purchaseIntentFor(params.item);
  const eventScoped = params.item.kind !== 'account_storage';
  const eventId = eventScoped ? params.event.id : null;
  const attributes: Record<string, string | null> = {
    pending_event_id: eventId,
    gathrd_event_id: eventId,
    event_id: eventId,
    pending_purchase_intent: intent,
    gathrd_purchase_intent: intent,
    purchase_intent: intent,
    pending_product_id: params.item.productId,
    gathrd_product_id: params.item.productId,
    pending_package_key: params.item.packageKey ?? null,
    gathrd_package_key: params.item.packageKey ?? null,
    pending_guest_user_id: params.item.kind === 'guest_video_pass' ? params.userId : null,
    gathrd_guest_user_id: params.item.kind === 'guest_video_pass' ? params.userId : null,
  };
  await purchases.setAttributes(attributes);

  const beforeUpgrade = eventScoped ? await loadEventUpgradeSummary(params.event.id).catch(() => null) : null;

  try {
    const purchaseResult = await purchases.purchase({
      rcPackage,
      customerEmail: params.email || undefined,
      metadata: {
        source: 'gathrd_web',
        gathrd_event_id: eventId,
        gathrd_purchase_intent: intent,
        gathrd_product_id: params.item.productId,
        gathrd_package_key: params.item.packageKey ?? null,
      },
    });

    const sync = await waitForCanonicalPurchaseState({
      item: params.item,
      event: params.event,
      userId: params.userId,
      beforeUpgrade,
    });
    return {
      cancelled: false,
      purchaseResult,
      syncConfirmed: sync.confirmed,
      syncMessage: sync.message,
    };
  } catch (error) {
    if (error instanceof PurchasesError && error.errorCode === ErrorCode.UserCancelledError) {
      return {
        cancelled: true,
        purchaseResult: null,
        syncConfirmed: false,
        syncMessage: 'Purchase cancelled.',
      };
    }
    throw error;
  }
}
